<template>
  <div class="i-hero-section">
    <div
      class="image-wrap"
      :style="{ backgroundImage: 'url(' + heroImage + ')' }"
    >
      <div class="main-item">
        <div class="text-items">
          <div class="first">
            <span>{{ `${COPY_DATA[testTakingPurposeKey].h1[0]} ` }}</span>
            <span>{{ COPY_DATA[testTakingPurposeKey].h1[1] }}</span>
          </div>
          <div class="second">20 minutes today will change your next 20 years.</div>
          <div
            v-if="isLargerThanTablet"
            class="assurance"
          >
            <iBaseButton
              v-bind="buttonStyle"
              @click="$emit('scrollToThreeKeys')"
            />
            <div class="rate">
              <img
                class="stars"
                :src="ImageStars"
              />100% money back guarantee
            </div>
            <div class="others-helped">
              <img
                class="laurels"
                :src="ImageLaurelLeft"
              />
              <img
                class="people"
                :src="ImageAvatars"
              />
              <span>850,000 people found direction—your turn is now.</span>
              <img
                class="laurels"
                :src="ImageLaurelRight"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!isLargerThanTablet"
      class="assurance"
    >
      <iBaseButton
        v-bind="buttonStyle"
        @click="$emit('scrollToThreeKeys')"
      />
      <div class="rate">
        <img
          class="stars"
          :src="ImageStars"
        />100% money back guarantee
      </div>
      <div class="others-helped">
        <img
          v-if="isLargerThanTablet"
          class="laurels"
          :src="ImageLaurelLeft"
        />
        <img
          class="people"
          :src="ImageAvatars"
        />
        <span>850,000 people found direction—your turn is now.</span>
        <img
          v-if="isLargerThanTablet"
          class="laurels"
          :src="ImageLaurelRight"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"
import { black950, bluePurpleGradient, sorbus400, white } from "@/styles/colors"
import { breakpointLg } from "@/styles/variables"
import { computed } from "vue"

import useIsScreenSize from "@/composables/useIsScreenSize"

import { COPY_DATA, IMAGE_DATA } from "@/helpers/startPageDynamicData"

import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import ImageAvatars from "@/assets/images/avatars.png"
import ImageStars from "@/assets/images/five-orange-stars.png"
import ImageLaurelLeft from "@/assets/images/laurel-left.png"
import ImageLaurelRight from "@/assets/images/laurel-right.png"

// EMITS
defineEmits(["scrollToThreeKeys"])

// STORES
const reportStore = useReportStore()
const userStore = useUserStore()

// PROPS
const props = defineProps({
  version: {
    type: String,
    default: "default",
    validator: (value: string) => value === "default" || value === "begin"
  }
})

// COMPOSABLES
const isLargerThanTablet = useIsScreenSize(breakpointLg)

//COMPUTED
const buttonStyle = computed(() => {
  return {
    backgroundColor: props.version === "begin" ? undefined : sorbus400,
    backgroundImage: props.version === "begin" ? bluePurpleGradient : undefined,
    borderRadius: "8px",
    fontWeight: "500",
    size: "medium",
    text: "Unlock Your Potential",
    textColor: props.version === "begin" ? white : black950,
    width: isLargerThanTablet.value ? "350px" : "100%",
    isHoverable: true,
    pulseColor: props.version === "begin" ? undefined : "#ffad328a"
  }
})
const testTakingPurposeKey = computed(() => {
  return (reportStore.getTestTakingPurpose?.id || "other") as keyof typeof COPY_DATA
})
const heroImage = computed(() => {
  const genderToUse = userStore.getGender == "male" ? "male" : "other"
  const ageRange = reportStore.reportData?.user?.currentAge.value < 30 ? "less30" : "over30"
  const screenWidth = isLargerThanTablet.value ? "desktop" : "mobile"
  return testTakingPurposeKey.value == "collegeMajor"
    ? IMAGE_DATA.collegeMajor[screenWidth]
    : IMAGE_DATA.other[genderToUse][ageRange][screenWidth]
})
</script>

<style scoped lang="scss">
.i-hero-section {
  padding: 20px 16px 0;
  margin-bottom: 50px;

  .image-wrap {
    @include flex(column, start, center);
    @include border-radius-xxl;

    background-image: url("https://jobtestorg-misc.s3.us-east-2.amazonaws.com/app-assets/CRO/careerSwitch%2Badvance%2Bai%2Blost%2Bother_male_less30_AR1x1.webp");
    background-size: cover;
    background-position: bottom;
    width: 100%;
    height: 510px;
    padding: 20px;

    @media screen and (min-width: $breakpoint-sm) {
      height: 600px;
    }

    @media screen and (min-width: $breakpoint-md) {
      height: 700px;
    }

    @media screen and (min-width: $breakpoint-lg) {
      @include flex(row, start, center);

      background-position: top;
      height: 600px;
      background-image: url("https://jobtestorg-misc.s3.us-east-2.amazonaws.com/app-assets/CRO/careerSwitch%2Badvance%2Bai%2Blost%2Bother_male_less30_AR2x1.webp");
    }

    .main-item {
      @include flex(row, center, center);

      width: 100%;

      @media screen and (min-width: $breakpoint-md) {
        width: 55%;
      }

      @media screen and (min-width: $breakpoint-xl) {
        @include flex(row, end, center);

        width: 45%;
      }

      .text-items {
        @include flex(column, center, center, $gap: 25px);

        text-align: center;
        max-width: 485px;

        .first {
          @include display-sm;

          font-weight: 700;

          @media screen and (min-width: $breakpoint-sm) {
            font-size: 40px;
            line-height: 50px;
          }
        }

        .second {
          @include text-xl;

          font-weight: 500;
          max-width: 310px;
        }
      }
    }
  }

  .assurance {
    @include flex(column, center, center, $gap: 20px);
    @include text-sm;

    font-weight: 600;
    margin-top: 20px;

    @media screen and (min-width: $breakpoint-lg) {
      margin-top: 10px;
    }

    .rate {
      @include flex(column, center, center, $gap: 10px);
    }

    .others-helped {
      @include flex(row, center, center);

      border-top: 1px solid $zumthor100;
      border-bottom: 1px solid $zumthor100;
      min-width: 468px;
      width: 100%;
      padding: 25px 15px;

      @media screen and (min-width: $breakpoint-lg) {
        @include border-radius-md;

        padding: 8px 15px;
        width: unset;
        background-color: rgb(255 255 255 / 20%);
        border-top: none;
        border-bottom: none;
      }

      span {
        max-width: 190px;
        text-align: left;
        margin-left: 8px;
      }
    }
  }
}
</style>
